/**
 * MSAL configuration for Entra ID sign-in.
 *
 * Canto uses a single Entra app registration for both the SPA and the API.
 * The SPA authenticates as that app registration (clientId) and requests the
 * `Canto.Access` delegated scope so the resulting access token carries the
 * correct audience for backend validation.
 *
 * Required Vite env vars (set in .env.local for dev, injected at build in CI):
 *   VITE_CANTO_ENTRA_TENANT_ID      — Ethereal Brewing tenant UUID
 *   VITE_CANTO_ENTRA_API_CLIENT_ID  — Canto app registration client UUID
 *   VITE_CANTO_ENTRA_API_SCOPE      — (optional) full scope string, e.g.
 *                                     api://{clientId}/Canto.Access
 *                                     defaults to api://{clientId}/Canto.Access
 */

import {
  PublicClientApplication,
  type Configuration,
  type RedirectRequest,
  LogLevel,
} from '@azure/msal-browser';

// ---------------------------------------------------------------------------
// Read + validate env vars at module init time so misconfiguration is caught
// immediately rather than silently at sign-in.
// ---------------------------------------------------------------------------

const tenantId = import.meta.env.VITE_CANTO_ENTRA_TENANT_ID as string | undefined;
const apiClientId = import.meta.env.VITE_CANTO_ENTRA_API_CLIENT_ID as string | undefined;

if (!tenantId || !apiClientId) {
  const missing: string[] = [];
  if (!tenantId) missing.push('VITE_CANTO_ENTRA_TENANT_ID');
  if (!apiClientId) missing.push('VITE_CANTO_ENTRA_API_CLIENT_ID');
  // In production a missing config is a hard failure. In test environments
  // (import.meta.env.MODE === 'test') we skip this because Vitest doesn't
  // load .env.local files.
  if (import.meta.env.MODE !== 'test') {
    throw new Error(
      `MSAL: required env vars missing: ${missing.join(', ')}. ` +
        'Create apps/web/.env.local — see .env.local.example.',
    );
  }
}

const resolvedTenantId = tenantId ?? 'test-tenant-id';
const resolvedClientId = apiClientId ?? 'test-client-id';

// The scope defaults to the standard `api://{clientId}/Canto.Access` pattern.
const scopeOverride = import.meta.env.VITE_CANTO_ENTRA_API_SCOPE as string | undefined;
export const apiScopes: string[] = [
  scopeOverride ?? `api://${resolvedClientId}/Canto.Access`,
];

// ---------------------------------------------------------------------------
// MSAL public client configuration
// ---------------------------------------------------------------------------

const msalConfig: Configuration = {
  auth: {
    clientId: resolvedClientId,
    authority: `https://login.microsoftonline.com/${resolvedTenantId}`,
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // localStorage persists sign-in across tab restarts and browser
    // restarts. Required for the iPad kiosk accounts that must stay
    // signed in indefinitely without re-authenticating.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[MSAL]', message);
        else if (level === LogLevel.Warning) console.warn('[MSAL]', message);
      },
    },
  },
};

/**
 * Singleton MSAL instance. Imported wherever a token acquisition is needed.
 * The MsalProvider wraps the React tree and subscribes to account events.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * The login redirect request. Used by AuthGuard when no account is found.
 * Includes the API scope so the redirect returns both an id_token and an
 * access_token in one round-trip.
 */
export const loginRequest: RedirectRequest = {
  scopes: apiScopes,
};

export { resolvedTenantId as tenantId, resolvedClientId as clientId };
